/**
 * Metrics Overview - Dashboard metric cards
 * @module components/starnexus/dashboard/MetricsOverview
 *
 */

import React, { useEffect, useState } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import Icon from '@amzn/awsui-components-console/icon';
import Spinner from '@amzn/awsui-components-console/spinner';

import { metricsService } from '../../../services/starnexus';
import { useResponsive } from '../../../hooks/starnexus/useResponsive';
import type { DashboardMetrics, MetricWithChange } from '../../../types/starnexus';

interface MetricCardProps {
  title: string;
  value: string | number;
  unit?: string;
  change?: number;
  changeType?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, unit, change, changeType }) => {
  const isPositive = changeType === 'positive';
  const changeColor = isPositive ? 'text-status-success' : 'text-status-error';
  const changeIcon = isPositive ? 'caret-up-filled' : 'caret-down-filled';

  return (
    <div>
      <Box variant="awsui-key-label">{title}</Box>
      <Box variant="h1" padding={{ top: 'xxs' }}>
        {value}
        {unit && (
          <Box variant="small" display="inline">
            {' '}
            {unit}
          </Box>
        )}
      </Box>
      {change !== undefined && (
        <Box color={changeColor} fontSize="body-s" padding={{ top: 'xxs' }}>
          <Icon name={changeIcon} size="small" /> {Math.abs(change)}
          {unit === '%' ? ' pts' : unit ? ` ${unit}` : ''}
          {' vs yesterday'}
        </Box>
      )}
    </div>
  );
};

// Type guard to check if metric is MetricWithChange
const isMetricWithChange = (metric: number | MetricWithChange): metric is MetricWithChange => {
  return typeof metric === 'object' && 'value' in metric;
};

export const MetricsOverview: React.FC = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const { metricColumns } = useResponsive();

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const data = await metricsService.getDashboardMetrics();
        setMetrics(data);
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <Container header={<Header variant="h2">Overview</Header>}>
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      </Container>
    );
  }

  if (!metrics) return null;

  // Extract values from metrics (handle both number and MetricWithChange types)
  const activeDatasets = isMetricWithChange(metrics.activeDatasets)
    ? metrics.activeDatasets
    : {
        value: metrics.activeDatasets,
        change: 0,
        changeType: 'neutral',
        label: 'Active Datasets'
      };

  const filesProcessed = isMetricWithChange(metrics.filesProcessedToday)
    ? metrics.filesProcessedToday
    : {
        value: metrics.filesProcessedToday,
        change: 0,
        changeType: 'neutral',
        label: 'Files Processed Today'
      };

  const successRate = isMetricWithChange(metrics.successRate)
    ? metrics.successRate
    : {
        value: metrics.successRate,
        change: 0,
        changeType: 'neutral',
        label: 'Success Rate',
        unit: '%'
      };

  const avgTime = isMetricWithChange(metrics.avgProcessingTime)
    ? metrics.avgProcessingTime
    : {
        value: metrics.avgProcessingTime,
        change: 0,
        changeType: 'neutral',
        label: 'Avg Processing Time',
        unit: 's'
      };

  return (
    <Container header={<Header variant="h2">Overview</Header>}>
      <ColumnLayout columns={metricColumns} variant="text-grid">
        <MetricCard title={activeDatasets.label} value={activeDatasets.value} change={activeDatasets.change} changeType={activeDatasets.changeType} />
        <MetricCard title={filesProcessed.label} value={filesProcessed.value.toLocaleString()} change={filesProcessed.change} changeType={filesProcessed.changeType} />
        <MetricCard title={successRate.label} value={successRate.value.toFixed(1)} unit="%" change={successRate.change} changeType={successRate.changeType} />
        <MetricCard title={avgTime.label} value={avgTime.value} unit="s" change={avgTime.change} changeType={avgTime.changeType} />
      </ColumnLayout>
    </Container>
  );
};

export default MetricsOverview;
